/**
 * Hook to sync workspace and profile data on app load
 * Fetches fresh data from API while keeping cached data for instant render
 */

import type { ProfileResponse } from '@dovydaskukalis/shared/types/api/profiles/responses';
import type { WorkspaceListResponse } from '@dovydaskukalis/shared/types/api/workspaces/responses';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { useAuthStore } from '@/features/auth/store/auth-store';
import { apiClient } from '@/lib/api/functions/api-client';

import { useWorkspaceStore } from '../store/workspace-store';

/**
 * Syncs workspace and profile data from server on mount
 * Runs silently in background - cached data renders immediately
 */
export function useSyncWorkspace(): void {
  const navigate = useNavigate();
  const hasSynced = useRef(false);

  const setHasWorkspace = useAuthStore((state) => state.setHasWorkspace);
  const currentWorkspace = useWorkspaceStore((state) => state.workspace);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const setProfile = useWorkspaceStore((state) => state.setProfile);
  const clearWorkspace = useWorkspaceStore((state) => state.clearWorkspace);

  useEffect(() => {
    // Only sync once per mount
    if (hasSynced.current) {
      return;
    }

    hasSynced.current = true;

    async function syncWorkspaceData(): Promise<void> {
      try {
        // Fetch all workspaces with profile info (NOT workspace-scoped)
        const response = await apiClient<WorkspaceListResponse>('/workspaces', {
          workspaceScoped: false,
        });

        // No workspaces - redirect to onboarding
        if (response.workspaces.length === 0) {
          clearWorkspace();
          setHasWorkspace(false);
          await navigate({ to: '/onboarding/create-workspace' });
          return;
        }

        // Find current workspace in the list (check if it still exists)
        const currentWorkspaceItem = currentWorkspace
          ? response.workspaces.find((w) => w.id === currentWorkspace.id)
          : undefined;

        // Use current workspace if it exists, otherwise use first workspace
        const selectedWorkspace = currentWorkspaceItem ?? response.workspaces[0];

        if (!selectedWorkspace) {
          // This should never happen since we checked length > 0, but TypeScript needs this
          throw new Error('No workspace found');
        }

        // Extract workspace data (remove profile field)
        const { profile: _profileData, ...workspaceData } = selectedWorkspace;

        // Fetch full profile for current workspace (workspace-scoped)
        const fullProfile = await apiClient<ProfileResponse>('/profiles/me', {
          workspaceId: selectedWorkspace.id,
        });

        // Update stores with fresh data
        setWorkspace(workspaceData);
        setProfile(fullProfile);
        setHasWorkspace(true);
      } catch (error) {
        // Log error but keep cached data on network failure
        /* c8 ignore next -- @preserve */
        console.error('Failed to sync workspace data:', error);
      }
    }

    // Run sync in background (non-blocking)
    void syncWorkspaceData();
  }, [navigate, setHasWorkspace, currentWorkspace, setWorkspace, setProfile, clearWorkspace]);
}
